// A task's photos, each with exactly one tag: before · progress · after.
//   task.shots  [{ uri, tag, at, takenAt?, caption? }]   the tagged list (new photos land here). `at` = when it was
//               added to the task; `takenAt` = when it was taken (camera = the same moment; a gallery pick = the
//               picture's own date) — the Daily Log puts it on the day and time it was taken. `caption` = the note
//               on the photo, typed or dictated as it's snapped (Lj, Oct 10: text only).
//   task.photo  the card photo — any one of them (legacy: a before photo on its own)
//   task.afterPhoto / afterPhotos    legacy after photos, read as 'after' unless a shot re-tags them
// Readers go through taskPhotoList; writers through the patch helpers below. Pure.
export const PHOTO_TAGS = ['before', 'progress', 'after'];
export const PHOTO_TAG_LABEL = { before: 'Before', progress: 'Progress', after: 'After' };
const ORDER = { before: 0, progress: 1, after: 2 };
const okTag = (t) => (PHOTO_TAGS.includes(t) ? t : 'progress');

/**
 * [{ uri, tag, at, addedAt, takenAt, caption, cover }] — one entry per picture, before → progress → after, oldest first
 * within a tag. `at` = when it was taken (else when added) — what timelines sort and file by.
 */
export function taskPhotoList(task) {
  if (!task) return [];
  const seen = new Map();
  const put = (uri, tag, at, extra = {}) => {
    if (typeof uri !== 'string' || !uri || seen.has(uri)) return;
    const takenAt = Number(extra.takenAt) > 0 ? Number(extra.takenAt) : null;
    seen.set(uri, { uri, tag, at: takenAt || at || 0, addedAt: at || 0, takenAt, caption: typeof extra.caption === 'string' && extra.caption.trim() ? extra.caption.trim() : null });
  };
  for (const s of Array.isArray(task.shots) ? task.shots : []) if (s && s.uri) put(s.uri, okTag(s.tag), s.at, s);
  put(task.photo, 'before', 0);
  put(task.afterPhoto, 'after', 0);
  for (const u of Array.isArray(task.afterPhotos) ? task.afterPhotos : []) put(u, 'after', 0);
  return [...seen.values()]
    .map((p) => ({ ...p, cover: p.uri === task.photo }))
    .sort((a, b) => ORDER[a.tag] - ORDER[b.tag] || a.at - b.at);
}

/** The tag a new shot gets by default: before while there's nothing, after once it's done, progress between. */
export function defaultShotTag(task) {
  if (!task || !taskPhotoList(task).length) return 'before';
  return task.status === 'done' ? 'after' : 'progress';
}

/** New shots [{ uri, tag, takenAt?, caption? }] → patch. The first one becomes the card photo when the task has none. */
export function addTaskShots(task, shots, now = Date.now()) {
  const list = (shots || []).filter((s) => s && s.uri);
  if (!list.length) return {};
  const cur = Array.isArray(task?.shots) ? task.shots : [];
  const have = new Set(cur.map((s) => s.uri));
  const add = list.filter((s) => !have.has(s.uri)).map((s, i) => {
    const shot = { uri: s.uri, tag: okTag(s.tag), at: now + i };
    if (Number(s.takenAt) > 0) shot.takenAt = Number(s.takenAt);
    if (typeof s.caption === 'string' && s.caption.trim()) shot.caption = s.caption.trim();
    return shot;
  });
  const out = { shots: [...cur, ...add] };
  if (!task?.photo && add.length) out.photo = (add.find((s) => s.tag === 'before') || add[0]).uri;
  return out;
}

/** One tag per photo: re-tag it (a legacy photo moves into shots so the new tag wins). */
export function retagTaskPhoto(task, uri, tag, now = Date.now()) {
  const cur = Array.isArray(task?.shots) ? task.shots : [];
  const t = okTag(tag);
  if (cur.some((s) => s.uri === uri)) return { shots: cur.map((s) => (s.uri === uri ? { ...s, tag: t } : s)) };
  return { shots: [...cur, { uri, tag: t, at: now }] };
}

/** Remove a picture from everywhere it's referenced; the card photo falls to the next one (before first). */
export function removeTaskPhoto(task, uri) {
  const out = {};
  const cur = Array.isArray(task?.shots) ? task.shots : [];
  if (cur.some((s) => s.uri === uri)) out.shots = cur.filter((s) => s.uri !== uri);
  if (Array.isArray(task?.afterPhotos) && task.afterPhotos.includes(uri)) out.afterPhotos = task.afterPhotos.filter((u) => u !== uri);
  if (task?.afterPhoto === uri) out.afterPhoto = (out.afterPhotos || task.afterPhotos || [])[0] || null;
  if (task?.photo === uri) {
    const rest = taskPhotoList({ ...task, ...out, photo: null });
    out.photo = (rest.find((p) => p.tag === 'before') || rest[0] || {}).uri || null;
  }
  return out;
}

/** Make a picture the card photo. A legacy cover that lives nowhere else is kept as a before shot. */
export function setTaskCover(task, uri, now = Date.now()) {
  if (!task || task.photo === uri) return {};
  const out = { photo: uri };
  const old = task.photo;
  const cur = Array.isArray(task.shots) ? task.shots : [];
  const elsewhere = cur.some((s) => s.uri === old) || (task.afterPhotos || []).includes(old) || task.afterPhoto === old;
  if (old && !elsewhere) out.shots = [...cur, { uri: old, tag: 'before', at: now }];
  return out;
}

/** The note on a photo (typed or dictated). Empty text clears it. A legacy photo moves into shots to carry it. */
export function captionTaskPhoto(task, uri, caption, now = Date.now()) {
  const text = String(caption ?? '').trim();
  const cur = Array.isArray(task?.shots) ? task.shots : [];
  const set = (s) => { const n = { ...s }; if (text) n.caption = text; else delete n.caption; return n; };
  if (cur.some((s) => s.uri === uri)) return { shots: cur.map((s) => (s.uri === uri ? set(s) : s)) };
  const legacy = taskPhotoList(task).find((p) => p.uri === uri);
  if (!legacy) return {};
  return { shots: [...cur, set({ uri, tag: legacy.tag, at: legacy.addedAt || now })] };
}

/**
 * When a picked picture was taken, from its EXIF (DateTimeOriginal "2026:10:09 14:12:03", local time) — null when
 * it doesn't say. A camera shot is taken now.
 */
export function exifTakenAt(exif) {
  const raw = exif?.DateTimeOriginal || exif?.DateTimeDigitized || exif?.DateTime || null;
  const m = typeof raw === 'string' ? raw.match(/^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/) : null;
  if (!m) return null;
  const t = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6])).getTime();
  return Number.isFinite(t) && t > 0 ? t : null;
}
